import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { WORKSPACE_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import type { WorkspacesResponse } from '@/types/workspace';

const QUERY_KEY = ['user_workspaces'];
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 10 * 60 * 1000; // 10 minutes

export const useWorkspaces = () => {
  return useQuery(QUERY_KEY, fetchWorkspaces, {
    select: (data) => data.workspaces,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
  });
};

export const usePrefetchWorkspaces = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    queryClient.prefetchQuery(QUERY_KEY, {
      queryFn: fetchWorkspaces,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  }, [queryClient]);
};

export const useRefetchWorkspaces = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    return queryClient.invalidateQueries(QUERY_KEY);
  }, [queryClient]);
};

const fetchWorkspaces = async ({ signal }: { signal?: AbortSignal }) => {
  const response = await axiosInstance.get<WorkspacesResponse>(
    WORKSPACE_API_ROUTES.baseRoute(),
    { signal }
  );
  return response.data;
};
